import { __assign } from 'tslib';
import React__default, { forwardRef } from 'react';
import { KpkApiThumbnailTemplateFormat } from '@keepeek/api-client';
import { Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, TextField, ToggleButtonGroup, ToggleButton, FormControlLabel, Checkbox, Button } from '@mui/material';
import clsx from 'clsx';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DefaultIcon from '../../DefaultIcon/index.js';
import { DEFAULT_ICON, Icons } from './utils.js';

var ManageTemplate = forwardRef(function ManageTemplate(_a, ref) {
    var className = _a.className, _b = _a.testId, testId = _b === void 0 ? "KFThumbnailGeneratorManageTemplate" : _b, sx = _a.sx, updateMode = _a.updateMode, _c = _a.KIcon, KIcon = _c === void 0 ? DefaultIcon : _c, _d = _a.defaultValues, defaultValues = _d === void 0 ? {
        icon: DEFAULT_ICON,
        name: "",
        width: "",
        height: "",
        format: KpkApiThumbnailTemplateFormat.Webp,
        shared: false
    } : _d, _e = _a.isAdmin, isAdmin = _e === void 0 ? false : _e, onSubmit = _a.onSubmit, onCancel = _a.onCancel;
    var t = useTranslation("refront-components").t;
    var _f = useForm({
        defaultValues: defaultValues
    }), handleSubmit = _f.handleSubmit, control = _f.control;
    var boxFormSx = {
        pb: 3
    };
    return (React__default.createElement(Box, { className: clsx("KFThumbnailGeneratorManageTemplate", className), "data-testid": testId, sx: __assign(__assign({}, sx), { height: "100%" }), ref: ref },
        React__default.createElement(Paper, { sx: { p: 1, height: "100%", label: { color: function (theme) { return theme.palette.grey[800]; } } }, elevation: 1, variant: "elevation" },
            React__default.createElement("form", { style: { height: "100%" }, onSubmit: handleSubmit(onSubmit) },
                React__default.createElement(Box, { sx: {
                        display: "flex",
                        flexDirection: "column",
                        height: "100%"
                    } },
                    React__default.createElement(Box, { sx: boxFormSx },
                        React__default.createElement(Typography, { variant: "h6" }, updateMode
                            ? t("components.kf-thumbnail-generator.edit.update.title")
                            : t("components.kf-thumbnail-generator.edit.create.title"))),
                    React__default.createElement(Box, { sx: boxFormSx },
                        React__default.createElement(Controller, { name: "icon", control: control, rules: {
                                required: t("components.kf-thumbnail-generator.edit.error.message.required").toString(),
                                maxLength: {
                                    value: 256,
                                    message: t("components.kf-thumbnail-generator.edit.error.message.oversize").toString()
                                }
                            }, render: function (_a) {
                                var _b = _a.field, onChange = _b.onChange, onBlur = _b.onBlur, value = _b.value, invalid = _a.fieldState.invalid;
                                return (React__default.createElement(FormControl, { sx: {
                                        width: "80px"
                                    } },
                                    React__default.createElement(InputLabel, { id: "icon-label" }, t("components.kf-thumbnail-generator.edit.icon")),
                                    React__default.createElement(Select, { size: "small", labelId: "icon-label", value: value, label: t("components.kf-thumbnail-generator.edit.icon"), "aria-label": t("components.kf-thumbnail-generator.edit.icon"), onChange: onChange, onBlur: onBlur, error: invalid }, Object.entries(Icons).map(function (icon) { return (React__default.createElement(MenuItem, { key: icon[1], value: icon[1], title: icon[0] },
                                        React__default.createElement(KIcon, { className: icon[1], sx: { fontSize: "1.3rem", paddingTop: "2px" } }))); }))));
                            } })),
                    React__default.createElement(Box, { sx: boxFormSx },
                        React__default.createElement(Controller, { name: "name", control: control, rules: {
                                required: t("components.kf-thumbnail-generator.edit.error.message.required").toString(),
                                maxLength: {
                                    value: 64,
                                    message: t("components.kf-thumbnail-generator.edit.error.message.oversize").toString()
                                }
                            }, render: function (_a) {
                                var _b = _a.field, onChange = _b.onChange, onBlur = _b.onBlur, value = _b.value, _c = _a.fieldState, invalid = _c.invalid, error = _c.error;
                                return (React__default.createElement(TextField, { size: "small", type: "text", label: t("components.kf-thumbnail-generator.edit.label"), sx: {
                                        width: "80%"
                                    }, onChange: onChange, onBlur: onBlur, value: value, error: invalid, helperText: error === null || error === void 0 ? void 0 : error.message }));
                            } })),
                    React__default.createElement(Box, { sx: {
                            display: "flex",
                            gap: function (theme) { return theme.spacing(0.5); },
                            alignItems: "flex-start",
                            justifyContent: "flex-start",
                            pb: 1
                        } },
                        React__default.createElement(Box, null,
                            React__default.createElement(Controller, { name: "width", control: control, rules: widthHeightRules(t), render: function (_a) {
                                    var _b = _a.field, onChange = _b.onChange, onBlur = _b.onBlur, value = _b.value, _c = _a.fieldState, invalid = _c.invalid, error = _c.error;
                                    return (React__default.createElement(TextField, { size: "small", type: "text", label: t("components.kf-thumbnail-generator.edit.width"), onChange: onChange, onBlur: onBlur, value: value, sx: {
                                            width: "120px"
                                        }, error: invalid, helperText: error === null || error === void 0 ? void 0 : error.message }));
                                } })),
                        React__default.createElement(Box, { sx: {
                                paddingTop: "0.7em"
                            } }, "x"),
                        React__default.createElement(Box, null,
                            React__default.createElement(Controller, { name: "height", control: control, rules: widthHeightRules(t), render: function (_a) {
                                    var _b = _a.field, onChange = _b.onChange, onBlur = _b.onBlur, value = _b.value, _c = _a.fieldState, invalid = _c.invalid, error = _c.error;
                                    return (React__default.createElement(TextField, { size: "small", type: "text", label: t("components.kf-thumbnail-generator.edit.height"), onChange: onChange, onBlur: onBlur, value: value, sx: {
                                            width: "120px"
                                        }, error: invalid, helperText: error === null || error === void 0 ? void 0 : error.message }));
                                } }))),
                    React__default.createElement(Box, { sx: {
                            pb: 1
                        } },
                        React__default.createElement(Controller, { name: "format", control: control, rules: {
                                required: t("components.kf-thumbnail-generator.edit.error.message.required").toString()
                            }, render: function (_a) {
                                var _b = _a.field, onChange = _b.onChange, onBlur = _b.onBlur, value = _b.value;
                                return (React__default.createElement(React__default.Fragment, null,
                                    React__default.createElement(InputLabel, null,
                                        React__default.createElement(Typography, { variant: "caption" }, t("components.kf-thumbnail-generator.edit.format"))),
                                    React__default.createElement(ToggleButtonGroup, { color: "primary", size: "small", exclusive: true, value: value, onChange: onChange, onBlur: onBlur }, Object.values(KpkApiThumbnailTemplateFormat)
                                        .filter(function (v) { return isNaN(Number(v)); })
                                        .map(function (value) { return (React__default.createElement(ToggleButton, { key: value, value: value }, value)); }))));
                            } })),
                    isAdmin && (React__default.createElement(Box, { sx: {
                            flexGrow: 1
                        } },
                        React__default.createElement(Controller, { name: "shared", control: control, render: function (_a) {
                                var _b = _a.field, onChange = _b.onChange, value = _b.value;
                                return (React__default.createElement(FormControlLabel, { control: React__default.createElement(Checkbox, { value: value, checked: value, onChange: onChange }), label: React__default.createElement(InputLabel, null,
                                        React__default.createElement(Typography, { variant: "caption" }, t("components.kf-thumbnail-generator.edit.shared"))) }));
                            } }))),
                    React__default.createElement(Box, { sx: {
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: function (theme) { return theme.spacing(1); }
                        } },
                        React__default.createElement(Button, { variant: "text", onClick: onCancel }, t("components.kf-thumbnail-generator.edit.action.cancel")),
                        React__default.createElement(Button, { variant: "outlined", type: "submit" }, updateMode
                            ? t("components.kf-thumbnail-generator.edit.action.update")
                            : t("components.kf-thumbnail-generator.edit.action.add"))))))));
});
var widthHeightRules = function (t) {
    return {
        required: t("components.kf-thumbnail-generator.edit.error.message.required").toString(),
        maxLength: {
            value: 11,
            message: t("components.kf-thumbnail-generator.edit.error.message.oversize").toString()
        },
        pattern: {
            value: /^\d+$/,
            message: t("components.kf-thumbnail-generator.edit.error.message.number").toString()
        },
        min: {
            value: 1,
            message: t("components.kf-thumbnail-generator.edit.error.message.positiveNumber").toString()
        }
    };
};

export { ManageTemplate as default };
//# sourceMappingURL=index.js.map
